// PropertyDetailsPage.jsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  fetchPropertyById,
  fetchSimilarProperties
} from '../apis/properties';
import { submitLead } from '../apis/leads';
import livingArea from '../assets/icons/aspect-ratio.png'
import bedroomIcon from '../assets/icons/bed-double.png'
import bathroomIcon from '../assets/icons/toilet-02.png'
import terraceIcon from '../assets/icons/terrace.png'
import seaviewIcon from '../assets/icons/ferry-boat.png'
import carIcon from '../assets/icons/car-05.png'
import calendar from '../assets/icons/calendar-03.png'
import mapIcon from '../assets/icons/map.png'
import electric from '../assets/icons/electric-plugs.png'
import { getCssBackgroundImage } from '../utils/image';


const formatPrice = (value, currency = "USD") =>
  value
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: 0
      }).format(value)
    : "—";

export default function PropertyDetailsPage() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [similarProperties, setSimilarProperties] = useState([]);
  const [panelState, setPanelState] = useState({
    description: true,
    terms: false,
    agent: false
  });
  const [isGalleryModalOpen, setGalleryModalOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLeadModalOpen, setLeadModalOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [leadStatus, setLeadStatus] = useState('');
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

    const navigate = useNavigate();

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

    const handleOpenDetails = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      navigate(`/properties/${property.id}`);
    };
    

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();

    fetchPropertyById(id, controller.signal)
      .then(setProperty)
      .catch(console.error);

    return () => controller.abort();
  }, [id]);

  useEffect(() => {
    if (!property?.id) return;

    const controller = new AbortController();
    fetchSimilarProperties(property.id, controller.signal)
      .then((data) => setSimilarProperties(data.slice(0, 3)))
      .catch((error) => {
        console.error('Unable to load similar properties', error);
        setSimilarProperties([]);
      });

    return () => controller.abort();
  }, [property]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sortedImages = useMemo(() => {
    if (!property?.images?.length) return [];
    return [...property.images].sort(
      (a, b) => (b.isCover === true) - (a.isCover === true)
    );
  }, [property]);

  const galleryImages = useMemo(
    () => sortedImages.slice(0, 4),
    [sortedImages]
  );

  const galleryGridStyle = {
    gridTemplateRows: '200px 200px',
    gridAutoRows: '200px'
  };

  const activeModalImage = sortedImages[activeImageIndex];

  const closeGalleryModal = useCallback(() => setGalleryModalOpen(false), []);

  const openGalleryModal = useCallback(
    (index = 0) => {
      if (!sortedImages.length) return;
      setActiveImageIndex(index);
      setGalleryModalOpen(true);
    },
    [sortedImages.length]
  );

  const showNextImage = useCallback(() => {
    if (!sortedImages.length) return;
    setActiveImageIndex((prev) => (prev + 1) % sortedImages.length);
  }, [sortedImages.length]);

  const showPreviousImage = useCallback(() => {
    if (!sortedImages.length) return;
    setActiveImageIndex((prev) =>
      (prev - 1 + sortedImages.length) % sortedImages.length
    );
  }, [sortedImages.length]);

  useEffect(() => {
    if (!isGalleryModalOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeGalleryModal();
      } else if (event.key === 'ArrowRight') {
        showNextImage();
      } else if (event.key === 'ArrowLeft') {
        showPreviousImage();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryModalOpen, closeGalleryModal, showNextImage, showPreviousImage]);

  useEffect(() => {
    setGalleryModalOpen(false);
    setActiveImageIndex(0);
  }, [property?.id]);

  if (!property)
    return (
      <>
        <Header />
        <main className="details-loading">Loading...</main>
        <Footer />
      </>
    );

  const priceFormatted = formatPrice(property.price, property.currency);
  const togglePanel = (panelKey) => {
    setPanelState((prev) => ({ ...prev, [panelKey]: !prev[panelKey] }));
  };

  const handleKeyToggle = (event, panelKey) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      togglePanel(panelKey);
    }
  };

  const termsText =
    property.termsAndConditions ||
    property.terms ||
    'Please contact us to learn more about the specific terms and conditions for this property.';

  const agentAssistantText =
    property.agentAssistant ||
    property.agentAssistantNotes ||
    property.agentInfo ||
    'Our agent assistant is ready to help you with tailored insights, availability, and next steps.';

  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${property.address} ${property.city} ${property.country}`
  )}`;

  const isCompactLeadModal = viewportWidth <= 640;
  const responsiveLeadModalStyles = {
    modal: {
      ...leadModalStyles.modal,
      width: isCompactLeadModal ? 'calc(100% - 24px)' : leadModalStyles.modal.width,
      borderRadius: isCompactLeadModal ? '20px' : leadModalStyles.modal.borderRadius,
      maxHeight: isCompactLeadModal ? '95vh' : leadModalStyles.modal.maxHeight,
      overflowY: isCompactLeadModal ? 'auto' : leadModalStyles.modal.overflowY
    },
    modalContent: {
      ...leadModalStyles.modalContent,
      gridTemplateColumns: isCompactLeadModal
        ? '1fr'
        : leadModalStyles.modalContent.gridTemplateColumns,
      minHeight: isCompactLeadModal ? 'auto' : leadModalStyles.modalContent.minHeight,
      padding: isCompactLeadModal ? '0 0 8px' : leadModalStyles.modalContent.padding || 0
    },
    formPanel: {
      ...leadModalStyles.formPanel,
      padding: isCompactLeadModal ? '20px' : leadModalStyles.formPanel.padding
    },
    formRow: {
      ...leadModalStyles.formRow,
      flexDirection: isCompactLeadModal ? 'column' : leadModalStyles.formRow.flexDirection
    }
  };

  const handleLeadChange = (event) => {
    const { name, value } = event.target;
    setLeadForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    setIsSubmittingLead(true);
    setLeadStatus('');

    try {
      await submitLead({
        propertyId: property.id,
        ...leadForm
      });
      setLeadStatus('Thank you! A Dream Home advisor will reach out shortly.');
      setLeadForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Unable to submit lead', error);
      setLeadStatus('We could not send your request. Please try again.');
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const closeLeadModal = () => {
    setLeadModalOpen(false);
    setLeadStatus('');
  };

  const renderGalleryImageButton = (image, className, index) => (
    <button
      type="button"
      className={`${className} gallery-image-button`}
      style={{ backgroundImage: getCssBackgroundImage(image.imageUrl), minHeight: 0 }}
      onClick={() => openGalleryModal(index)}
      aria-label={`Open image ${index + 1} of ${sortedImages.length}`}
    />
  );

  return (
    <div className="details-page">
      <Header />
      <main className="details-container">

<div className='details-sub-container'>
        <p className="details-category">{property.category?.name}</p>

        {/* --- GALLERY --- */}
        <section className="gallery-grid" style={galleryGridStyle}>
          {galleryImages[0] && renderGalleryImageButton(galleryImages[0], 'gallery-main', 0)}
          {galleryImages[1] && renderGalleryImageButton(galleryImages[1], 'gallery-side', 1)}
          {galleryImages[2] && renderGalleryImageButton(galleryImages[2], 'gallery-small', 2)}
          {galleryImages[3] && renderGalleryImageButton(galleryImages[3], 'gallery-small', 3)}
        </section>

        {/* --- TITLE + META + PRICE BOX --- */}
        <section className="details-header">
          <div>
            <p className="details-reference">Reference | {property.referenceCode}</p>
            <h1 className="details-title">
              {property.title} , {property.country}
            </h1>
            <p className="details-address">{property.address}</p>
          </div>
          <a className="details-map" href={mapUrl} target="_blank" rel="noreferrer">
          <img className="icon-image" src={mapIcon} alt="" />View on map
            </a>
       
        </section>

        {/* --- MAP + INLINE STATS + PANELS + PRICE BOX --- */}
        <section className="details-body">
          
          <div className="details-left">
            <hr/>
         
            <div className="details-inline-stats">
              <div className="details-flex"><img className="icon-image" src={livingArea} alt="" /><p >{property.livingArea} sqm Living area</p></div>
              {property.dpe && <div className="details-flex"><img className="icon-image" src={electric} alt="" /><p >{property.dpe} DPE</p></div>}
              <div className="details-flex"><img className="icon-image"  src={bedroomIcon} alt="" /><p>{property.bedrooms} Bedrooms</p></div>
              <div className="details-flex"><img className="icon-image"  src={bathroomIcon} alt="" /><p>{property.bathrooms} Bathrooms</p></div>
              {property.terraces != 0 && <div className="details-flex"><img className="icon-image"  src={terraceIcon} alt="" /><p>{property.terraces} sqm Terraces</p></div>}
              {property.seaView && <div className="details-flex"><img className="icon-image"  src={seaviewIcon} alt="" /><p>Sea view</p></div>}
              {property.garageSpaces !=0 && <div className="details-flex"><img className="icon-image"  src={carIcon} alt="" /><p>{property.garageSpaces} Cars</p></div>}
              {property.buildYear && property.buildYear > 0 ? <div className="details-flex"><img className="icon-image"  src={calendar} alt="" /><p>Built {property.buildYear}</p></div>:<p></p>}
            </div>

            <div className="details-panels">
              <div className={`detail-panel ${panelState.description ? '' : 'collapsed'}`}>
                <div className="panel-header">
                  <p>Description</p>
                  <span
                    role="button"
                    className='arrow-details'
                    tabIndex={0}
                    onClick={() => togglePanel('description')}
                    onKeyDown={(event) => handleKeyToggle(event, 'description')}
                    aria-expanded={panelState.description}
                  >
                    {panelState.description ? '▾' : '▸'}
                  </span>
                </div>
                {panelState.description && (
                  <p className="panel-body">{property.description}</p>
                )}
              </div>

              <div className={`detail-panel ${panelState.terms ? '' : 'collapsed'}`}>
                <div className="panel-header">
                  <p>Terms & Conditions</p>
                  <span
                    role="button"
                    className='arrow-details'
                    tabIndex={0}
                    onClick={() => togglePanel('terms')}
                    onKeyDown={(event) => handleKeyToggle(event, 'terms')}
                    aria-expanded={panelState.terms}
                  >
                    {panelState.terms ? '▾' : '▸'}
                  </span>
                </div>
                {panelState.terms && (
                  <p className="panel-body">{termsText}</p>
                )}
              </div>

              <div className={`detail-panel ${panelState.agent ? '' : 'collapsed'}`}>
                <div className="panel-header">
                  <div className='agent-flex'>
                  {property.agentImage && <img className='image-agent' src={property.agentImage} />}
                  <p>Agent Assistant</p>
                  </div>
                  <span
                    role="button"
                    className='arrow-details'
                    tabIndex={0}
                    onClick={() => togglePanel('agent')}
                    onKeyDown={(event) => handleKeyToggle(event, 'agent')}
                    aria-expanded={panelState.agent}
                  >
                    {panelState.agent ? '▾' : '▸'}
                  </span>
                </div>
                {panelState.agent && (
                  <p className="panel-body">{agentAssistantText}</p>
                )}
              </div>
            </div>
          </div>

          <aside className="details-price-card">
            <p className="details-price-label">Price</p>
            <p className="details-price">{priceFormatted}</p>
            <ul>
              <li>Payment period {property.paymentPeriodYears} years</li>
              <li>Annual stay {property.annualStayDays} days</li>
            </ul>
            <button
              className="details-primary-action"
              onClick={() => setLeadModalOpen(true)}
            >
              Buy this house
            </button>
          </aside>
        </section>
        </div>
<section className="similar-box">
  <h2 className="similar-heading">Similar Properties</h2>
  <p className="similar-subtitle">Discover other properties that might be suitable for you.</p>

  <div className="similar-grid">
    {similarProperties.map((item) => {
      const coverImage =
        item.images?.find((img) => img.isCover)?.imageUrl || item.images?.[0]?.imageUrl || "";

      return (
        <Link className="similar-card" to={`/properties/${item.id}`} key={item.id}>
             <p className="similar-title">
              {item.country} <span className="similar-price">·     {formatPrice(item.price, item.currency)}</span>
            </p>
          <img src={coverImage} className="similar-image" alt="" />

          <div className="similar-body">
            {/* ICON ROWS */}
            <div className="similar-stats">
              <div className="stat-row">
              <div className="details-flex">
                  <img src={livingArea} className="icon-image" alt="" />
                  <span className="icon-text">{item.livingArea} sqm Living area</span>
                </div>
                <div className="stat-item">
                  <img src={bedroomIcon} className="icon-image" alt="" />
                  <span>{item.bedrooms} Bedrooms</span>
                </div>
                <div className="stat-item">
                  <img src={bathroomIcon} className="icon-image" alt="" />
                  <span>{item.bathrooms} Bathrooms</span>
                </div>
              </div>

              <div className="stat-row">
                <div className="stat-item">
                  <img src={terraceIcon} className="icon-image" alt="" />
                  <span>{item.terraces} sqm Terraces</span>
                </div>
                <div className="stat-item">
                  <img src={seaviewIcon} className="icon-image" alt="" />
                  <span>{item.seaView ? "Sea view" : "No sea view"}</span>
                </div>
                <div className="stat-item">
                  <img src={carIcon} className="icon-image" alt="" />
                  <span>{item.garageSpaces} Cars</span>
                </div>
              </div>
            </div>

            {/* VIEW DETAILS */}
            <span className="similar-link" onClick={handleOpenDetails}>View details »</span>
          </div>
        </Link>
      );
    })}
  </div>
</section>
      </main>
      {isGalleryModalOpen && sortedImages.length > 0 && (
        <div className="image-modal-overlay" onClick={closeGalleryModal}>
          <div
            className="image-modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="image-modal-close"
              onClick={closeGalleryModal}
              aria-label="Close gallery"
            >
              ×
            </button>
            {sortedImages.length > 1 && (
              <>
                <button
                  type="button"
                  className="image-modal-nav image-modal-nav--prev"
                  onClick={(event) => {
                    event.stopPropagation();
                    showPreviousImage();
                  }}
                  aria-label="View previous image"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="image-modal-nav image-modal-nav--next"
                  onClick={(event) => {
                    event.stopPropagation();
                    showNextImage();
                  }}
                  aria-label="View next image"
                >
                  ›
                </button>
              </>
            )}
            {activeModalImage && (
              <>
                <img
                  src={activeModalImage.imageUrl}
                  alt={activeModalImage.description || property.title || 'Property image'}
                />
                <p className="image-modal-counter">
                  {activeImageIndex + 1} / {sortedImages.length}
                </p>
              </>
            )}
          </div>
        </div>
      )}
      {isLeadModalOpen && (
        <div style={leadModalStyles.overlay}>
          <div style={responsiveLeadModalStyles.modal}>
            <h2 style={{textAlign:'center'}}>Contact Information</h2>
            <button style={leadModalStyles.closeButton} onClick={closeLeadModal}>
              ×
            </button>
            <div style={responsiveLeadModalStyles.modalContent}>
         

              <form style={responsiveLeadModalStyles.formPanel} onSubmit={handleLeadSubmit}>
                <div style={responsiveLeadModalStyles.formRow}>
                  <div style={leadModalStyles.formField}>
                    <label htmlFor="firstName">First name</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={leadForm.firstName}
                      onChange={handleLeadChange}
                      required
                      style={leadModalStyles.input}
                    />
                  </div>
                  <div style={leadModalStyles.formField}>
                    <label htmlFor="lastName">Last name</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={leadForm.lastName}
                      onChange={handleLeadChange}
                      required
                      style={leadModalStyles.input}
                    />
                  </div>
                </div>
                <div style={responsiveLeadModalStyles.formRow}>
                  <div style={leadModalStyles.formField}>
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={leadForm.email}
                      onChange={handleLeadChange}
                      required
                      style={leadModalStyles.input}
                    />
                  </div>
                  <div style={leadModalStyles.formField}>
                    <label htmlFor="phone">Phone</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={leadForm.phone}
                      onChange={handleLeadChange}
                      required
                      style={leadModalStyles.input}
                    />
                  </div>
                </div>
                <div style={leadModalStyles.formField}>
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={leadForm.message}
                    onChange={handleLeadChange}
                    style={leadModalStyles.textarea}
                    placeholder="Share timing, financing needs, or questions for the agent."
                  />
                </div>
                <button type="submit" style={leadModalStyles.submitButton} disabled={isSubmittingLead}>
                  {isSubmittingLead ? 'Sending...' : 'Submit'}
                </button>
                {leadStatus && (
                  <p
                    style={{
                      margin: '8px 0 0',
                      fontSize: '0.95rem',
                      color: leadStatus.startsWith('Thank') ? '#10b981' : '#f87171'
                    }}
                  >
                    {leadStatus}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

const leadModalStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  modal: {
    background: '#fff',
    borderRadius: '32px',
    width: 'min(960px, 100%)',
    maxHeight: '90vh',
    position: 'relative',
    boxShadow: '0 45px 75px rgba(15,23,42,0.25)',
    overflow: 'hidden',
    overflowY: 'visible'
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    border: 'none',
    background: 'transparent',
    fontSize: '1.5rem',
    cursor: 'pointer',
    lineHeight: 1
  },
  modalContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '0',
    minHeight: '480px'
  },
  imagePanel: {
    background: '#111827',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column'
  },
  image: {
    width: '100%',
    height: '60%',
    objectFit: 'cover'
  },
  imageText: {
    padding: '24px',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  formPanel: {
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  formRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    flexDirection: 'row'
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
    minWidth: '150px'
  },
  input: {
    borderRadius: '14px',
    border: '1px solid #e5e7eb',
    padding: '12px 14px',
    fontSize: '1rem'
  },
  textarea: {
    borderRadius: '14px',
    border: '1px solid #e5e7eb',
    padding: '12px 14px',
    fontSize: '1rem',
    minHeight: '120px',
    resize: 'vertical'
  },
  submitButton: {
    background: 'black',
    color: 'rgb(255, 255, 255)',
    border: 'none',
    borderRadius: '15px',
    padding: '16px 24px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%'
  }
};
